/**
 * Event-scoping for Elasticsearch listing queries.
 *
 * Every listing query in the app restricts results to events the current user is
 * allowed to see. That restriction used to be a `must_not` deny-list built from
 * `unauthorizedEventIds`, which the client derived itself by diffing the full event
 * list against `authorized-event-list`.
 *
 * That approach could only ever encode rules the client knew how to compute, and it
 * knew exactly one: "private, and I'm not a member". It had no idea about
 * `Users.PublicSaleOnly`, so public-only dealers browsed dealer events and dealer
 * inventory freely and were not stopped until they tried to bid (MP-2363).
 *
 * It is now an allow-list. `event-list` returns `authorizedEventIds` and the client
 * relays it without interpreting it, so the whole visibility policy lives in
 * `EventService.GetAuthorizedEventIds` and new rules are a server-only change.
 *
 * Two consequences worth knowing:
 *
 * 1. It fails closed. An empty or missing allow-list matches nothing, where an empty
 *    deny-list matched everything. Callers must not issue listing queries before the
 *    allow-list has loaded — the protected routes in `src/router/index.js` already
 *    gate rendering on that.
 * 2. On its own it is a UX filter, not an enforcement boundary: a browser that posts
 *    query DSL straight to Elasticsearch can strip it. Where `searchRoot` points at
 *    marketplace-api's `/api/search/` (MP-2365), the server adds the same allow-list to
 *    every query itself (SearchQueryPolicy), so stripping it here changes nothing. The
 *    client clause stays until every marketplace has moved, so behaviour is identical on
 *    either path; after that it is redundant and can go.
 */

/**
 * Elasticsearch clause restricting a listing query to authorized events.
 *
 * Belongs in `bool.must` (or `bool.filter`), replacing the old
 * `must_not: [{ terms: { eventId: unauthorizedEventIds } }]` entry.
 *
 * @param {number[]} authorizedEventIds ids from the `event-list` response
 * @returns {object} an ES bool clause
 */
export const authorizedEventsClause = (authorizedEventIds = []) => ({
  bool: {
    should: [
      { terms: { eventId: authorizedEventIds || [] } },

      // A listing not attached to any event carries no event-level restriction, so an
      // event allow-list must not silently delete it. `dbo.IsBuyer` short-circuits on
      // the same test (`if @EventId is null or @EventId = 0 return 1`), which is where
      // these listings come from. Under the old deny-list they were always included;
      // without these two branches, switching to an allow-list would quietly drop them.
      { term: { eventId: 0 } },
      { bool: { must_not: { exists: { field: 'eventId' } } } },
    ],
    minimum_should_match: 1,
  },
});

/**
 * Item count per marketplace, summed from the events the user is authorized to see.
 *
 * The count on a marketplace card used to come from `marketplace-list`, computed by
 * `dbo.GetSubMarketplaces` as:
 *
 *     count(l.ListingId)
 *     from Listings l join Events e on e.eventid = l.eventid
 *     where l.MarketplaceId = m.MarketplaceId
 *       and l.Deleted = 0 and l.status in (1) and e.PrivateEvent = 0
 *
 * That query takes no user context, so it was wrong in both directions: it hid
 * private-event inventory from dealers who *are* authorized for those events, while
 * counting dealer-event inventory for public-only dealers who are not (MP-2363). The
 * card therefore advertised a total the click-through could not produce.
 *
 * Deriving it from `events.eventList` instead costs no extra request and is scoped by
 * construction, because event-list is already filtered server-side.
 *
 * `itemCountAtAuction` is the right field to sum: it is status 1, matching the `status
 * in (1)` the proc used. The event-level `itemCount` is status in (1,3,5) and would
 * inflate the card — no-sales are a separate figure (`itemCountWithNoSales`).
 *
 * Known differences from the old number, all of them deliberate:
 *   - bulk-lot children are excluded (event counts filter ParentListingId = 0)
 *   - ended, cancelled and feature-38 events are excluded
 *   - private events the user IS authorized for are now included
 *
 * Grouping by the event's marketplaceId matches the proc's `l.MarketplaceId` rather
 * than diverging from it: co-listing only inserts rows into ColistedEvents(EventId,
 * ToMarketplaceId), never duplicating Listings, so a listing's MarketplaceId stays the
 * owning marketplace — the same value as its event's.
 *
 * A co-listing gap does exist, in both the old count and this one. Listings reach a
 * target marketplace's search index via ColistedEvents (see SimulcastSearch
 * Queries.cs), so marketplace B's search results include items from an event owned by
 * A, while neither count attributes them to B's card. B therefore undercounts what B's
 * page shows. Closing that needs event-list to report the marketplaces an event was
 * co-listed into — a list, not a single id, since ColistedEvents is one-to-many.
 *
 * @param {object[]} eventList `events.eventList` from the event-list response
 * @returns {Map<number, number>} marketplaceId -> summed itemCountAtAuction
 */
export const itemCountsByMarketplace = (eventList = []) => {
  const counts = new Map();

  (eventList || []).forEach(event => {
    const marketplaceId = Number(event.marketplaceId);
    if (!marketplaceId) return;

    const current = counts.get(marketplaceId) || 0;
    counts.set(marketplaceId, current + (event.itemCountAtAuction || 0));
  });

  return counts;
};
